/**
 * CashflowTab — payment history and reconciliation for coaches.
 *
 * Two tabs:
 *   Sessions — per-appointment payment history
 *   Packages — package payment history (installments, setup fees, pay-in-full)
 *
 * Status filter is a multiselect dropdown; filtering + summary are computed client-side
 * so the summary cards always reflect the current filtered view.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCashflowReport,
  getPackageCashflowReport,
  formatCents,
  recordPaymentFailure,
  refundSessionPayment,
  type CashflowSession,
  type PackagePayment,
} from '../services/paymentService';

// ── Types ──────────────────────────────────────────────────────────────────────

type TabId = 'all' | 'sessions' | 'packages';
type SessStatus = 'no_rate' | 'unpaid' | 'pending' | 'succeeded' | 'failed' | 'refunded' | 'waived';
type PkgStatus  = 'pending' | 'paid' | 'manual' | 'failed' | 'refunded';
type AllStatus  = 'paid' | 'unpaid' | 'pending' | 'failed' | 'refunded' | 'waived' | 'no_rate';
type ActionType = 'record-failure' | 'refund' | null;

interface UnifiedRow {
  id: string;
  rowType: 'session' | 'package';
  date: string | null;
  client_id: string;
  client_name: string;
  description: string;
  amount_cents: number | null;
  currency: string;
  normalStatus: AllStatus;
  payment_method: string | null;
  stripe_fee_cents: number | null;
  net_cents: number | null;
  paid_at: string | null;
  rawSession?: CashflowSession;
  rawPackage?: PackagePayment;
}

const SESS_STATUSES: { value: SessStatus; label: string }[] = [
  { value: 'no_rate',   label: 'No rate set' },
  { value: 'unpaid',    label: 'Unpaid' },
  { value: 'pending',   label: 'Pending' },
  { value: 'succeeded', label: 'Paid' },
  { value: 'failed',    label: 'Failed' },
  { value: 'refunded',  label: 'Refunded' },
  { value: 'waived',    label: 'Waived' },
];

const PKG_STATUSES: { value: PkgStatus; label: string }[] = [
  { value: 'pending',  label: 'Pending' },
  { value: 'paid',     label: 'Paid (legacy)' },
  { value: 'manual',   label: 'Paid (manual)' },
  { value: 'failed',   label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

const ALL_STATUSES: { value: AllStatus; label: string }[] = [
  { value: 'paid',     label: 'Paid' },
  { value: 'unpaid',   label: 'Unpaid' },
  { value: 'pending',  label: 'Pending' },
  { value: 'failed',   label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'waived',   label: 'Waived' },
  { value: 'no_rate',  label: 'No rate set' },
];

// ── MultiSelectDropdown ────────────────────────────────────────────────────────

interface MultiSelectProps<T extends string> {
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (next: T[]) => void;
  placeholder?: string;
}

function MultiSelectDropdown<T extends string>({ options, selected, onChange, placeholder = 'All statuses' }: MultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutsideClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, []);

  function toggle(value: T) {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  }

  const label = selected.length === 0
    ? placeholder
    : selected.length === options.length
    ? placeholder
    : selected.map(v => options.find(o => o.value === v)?.label ?? v).join(', ');

  const isAll = selected.length === 0 || selected.length === options.length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className={`flex items-center gap-1.5 bg-dark-sidebar border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 whitespace-nowrap max-w-[200px] ${
          isAll ? 'border-dark-border' : 'border-primary-500/60'
        }`}
      >
        <span className="truncate">{label}</span>
        <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 z-30 bg-dark-card border border-dark-border rounded-lg shadow-xl min-w-[160px] py-1">
          <button
            type="button"
            onClick={() => onChange([])}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 hover:bg-dark-hover transition-colors"
          >
            <span className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center ${isAll ? 'bg-primary-600 border-primary-500' : 'border-gray-600'}`}>
              {isAll && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
            </span>
            All statuses
          </button>
          <div className="h-px bg-dark-border mx-2 my-1" />
          {options.map(opt => {
            const checked = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-300 hover:bg-dark-hover transition-colors"
              >
                <span className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center ${checked ? 'bg-primary-600 border-primary-500' : 'border-gray-600'}`}>
                  {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function statusLabel(status: CashflowSession['payment_status']): { label: string; cls: string } {
  switch (status) {
    case 'succeeded': return { label: 'Paid',            cls: 'bg-green-900/40 text-green-400 border-green-800' };
    case 'manual':    return { label: 'Paid',            cls: 'bg-green-900/40 text-green-400 border-green-800' };
    case 'pending':   return { label: 'Pending',        cls: 'bg-yellow-900/40 text-yellow-400 border-yellow-800' };
    case 'unpaid':    return { label: 'Unpaid',         cls: 'bg-red-900/40 text-red-400 border-red-800' };
    case 'failed':    return { label: 'Failed',         cls: 'bg-red-900/40 text-red-400 border-red-800' };
    case 'refunded':  return { label: 'Refunded',       cls: 'bg-gray-800 text-gray-400 border-gray-700' };
    case 'waived':    return { label: 'Waived',         cls: 'bg-gray-800 text-gray-400 border-gray-700' };
    case 'no_rate':   return { label: 'No rate set',    cls: 'bg-gray-800 text-gray-500 border-gray-700' };
    default:          return { label: status,           cls: 'bg-gray-800 text-gray-400 border-gray-700' };
  }
}

function pkgStatusLabel(status: PackagePayment['payment_status']): { label: string; cls: string } {
  switch (status) {
    case 'paid':
    case 'manual':
    case 'succeeded': return { label: 'Paid',     cls: 'bg-green-900/40 text-green-400 border-green-800' };
    case 'pending':   return { label: 'Pending',  cls: 'bg-yellow-900/40 text-yellow-400 border-yellow-800' };
    case 'failed':    return { label: 'Failed',   cls: 'bg-red-900/40 text-red-400 border-red-800' };
    case 'refunded':  return { label: 'Refunded', cls: 'bg-gray-800 text-gray-400 border-gray-700' };
    default:          return { label: status,     cls: 'bg-gray-800 text-gray-400 border-gray-700' };
  }
}

function pkgPaymentTypeLabel(type: PackagePayment['payment_type'], num: number | null): string {
  if (type === 'setup_fee') return 'Setup fee';
  if (type === 'pay_in_full') return 'Pay in full';
  if (type === 'installment') return num != null ? `Payment #${num}` : 'Payment';
  return type;
}

function normalizeSessionStatus(s: CashflowSession['payment_status']): AllStatus {
  if (s === 'succeeded' || s === 'manual') return 'paid';
  if (s === 'unpaid')   return 'unpaid';
  if (s === 'pending')  return 'pending';
  if (s === 'failed')   return 'failed';
  if (s === 'refunded') return 'refunded';
  if (s === 'waived')   return 'waived';
  if (s === 'no_rate')  return 'no_rate';
  return 'pending';
}

function normalizePkgStatus(s: PackagePayment['payment_status']): AllStatus {
  if (s === 'paid' || s === 'manual' || s === 'succeeded') return 'paid';
  if (s === 'pending')  return 'pending';
  if (s === 'failed')   return 'failed';
  if (s === 'refunded') return 'refunded';
  return 'pending';
}

function allStatusLabel(s: AllStatus): { label: string; cls: string } {
  switch (s) {
    case 'paid':     return { label: 'Paid',         cls: 'bg-green-900/40 text-green-400 border-green-800' };
    case 'unpaid':   return { label: 'Unpaid',        cls: 'bg-red-900/40 text-red-400 border-red-800' };
    case 'pending':  return { label: 'Pending',       cls: 'bg-yellow-900/40 text-yellow-400 border-yellow-800' };
    case 'failed':   return { label: 'Failed',        cls: 'bg-red-900/40 text-red-400 border-red-800' };
    case 'refunded': return { label: 'Refunded',      cls: 'bg-gray-800 text-gray-400 border-gray-700' };
    case 'waived':   return { label: 'Waived',        cls: 'bg-gray-800 text-gray-400 border-gray-700' };
    case 'no_rate':  return { label: 'No rate set',   cls: 'bg-gray-800 text-gray-500 border-gray-700' };
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function exportSessionsCsv(sessions: CashflowSession[]): void {
  const headers = ['Session Date', 'Client', 'Session', 'Duration (min)', 'Session Fee', 'Status', 'Payment Method', 'Failure Reason', 'Payment Date', 'Payment Received', 'Stripe Fee', 'Net'];
  const rows = sessions.map(s => [
    s.scheduled_at ? formatDate(s.scheduled_at) : '',
    s.client_name,
    s.meeting_name,
    s.duration_minutes,
    s.rate_cents != null ? (s.rate_cents / 100).toFixed(2) : '',
    s.payment_status === 'manual' ? 'paid' : s.payment_status,
    s.payment_status === 'succeeded' ? 'Stripe' : s.payment_status === 'manual' ? (s.payment_method || 'Manual') : '',
    s.failure_reason || '',
    s.paid_at ? formatDateTime(s.paid_at) : '',
    s.amount_collected_cents != null ? (s.amount_collected_cents / 100).toFixed(2) : '',
    s.stripe_fee_cents != null ? (s.stripe_fee_cents / 100).toFixed(2) : '',
    s.net_cents != null ? (s.net_cents / 100).toFixed(2) : '',
  ]);
  downloadCsv([headers, ...rows], `cashflow_sessions_${new Date().toISOString().slice(0, 10)}.csv`);
}

function exportPackagesCsv(payments: PackagePayment[]): void {
  const headers = ['Client', 'Package', 'Payment Type', 'Amount', 'Status', 'Due Date', 'Paid At', 'Failure Reason'];
  const rows = payments.map(p => [
    p.client_name,
    p.package_name,
    pkgPaymentTypeLabel(p.payment_type, p.installment_number),
    (p.amount_cents / 100).toFixed(2),
    p.payment_status,
    p.due_date ? formatDate(p.due_date) : '',
    p.paid_at ? formatDateTime(p.paid_at) : '',
    p.failure_reason || '',
  ]);
  downloadCsv([headers, ...rows], `cashflow_packages_${new Date().toISOString().slice(0, 10)}.csv`);
}

function exportUnifiedCsv(rows: UnifiedRow[]): void {
  const headers = ['Date', 'Client', 'Type', 'Description', 'Amount', 'Status', 'Payment Method', 'Stripe Fee', 'Net', 'Payment Date'];
  const data = rows.map(r => [
    r.date ? formatDate(r.date) : '',
    r.client_name,
    r.rowType === 'session' ? 'Session' : 'Package',
    r.description,
    r.amount_cents != null ? (r.amount_cents / 100).toFixed(2) : '',
    r.normalStatus,
    r.payment_method || '',
    r.stripe_fee_cents != null ? (r.stripe_fee_cents / 100).toFixed(2) : '',
    r.net_cents != null ? (r.net_cents / 100).toFixed(2) : '',
    r.paid_at ? formatDateTime(r.paid_at) : '',
  ]);
  downloadCsv([headers, ...data], `cashflow_all_${new Date().toISOString().slice(0, 10)}.csv`);
}

function downloadCsv(rows: unknown[][], filename: string): void {
  const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  clients?: { client_id: string; client_name: string }[];
}

const CashflowTab: React.FC<Props> = ({ clients = [] }) => {
  const [activeTab, setActiveTab] = useState<TabId>('all');

  // Sessions — raw data + filters
  const [allSessions, setAllSessions]   = useState<CashflowSession[]>([]);
  const [sessLoading, setSessLoading]   = useState(false);
  const [sessError, setSessError]       = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [clientId, setClientId]         = useState('');
  const [sessStatuses, setSessStatuses] = useState<SessStatus[]>([]);
  const [groupByClient, setGroupByClient] = useState(false);
  const [groupByMonth, setGroupByMonth]   = useState(false);

  // Action dialog (sessions only)
  const [actionAppt, setActionAppt]       = useState<CashflowSession | null>(null);
  const [actionType, setActionType]       = useState<ActionType>(null);
  const [actionReason, setActionReason]   = useState('');
  const [actionChannel, setActionChannel] = useState<'stripe' | 'manual'>('manual');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState('');

  // Packages — raw data + filters
  const [allPkgPayments, setAllPkgPayments] = useState<PackagePayment[]>([]);
  const [pkgLoading, setPkgLoading]         = useState(false);
  const [pkgError, setPkgError]             = useState('');
  const [pkgDateFrom, setPkgDateFrom]       = useState('');
  const [pkgDateTo, setPkgDateTo]           = useState('');
  const [pkgClientId, setPkgClientId]       = useState('');
  const [pkgStatuses, setPkgStatuses]       = useState<PkgStatus[]>([]);

  // All tab — own data + filters (independent of sessions/packages tabs)
  const [allTabSessions, setAllTabSessions]   = useState<CashflowSession[]>([]);
  const [allTabPkgs, setAllTabPkgs]           = useState<PackagePayment[]>([]);
  const [allTabLoading, setAllTabLoading]     = useState(false);
  const [allTabError, setAllTabError]         = useState('');
  const [allTabDateFrom, setAllTabDateFrom]   = useState('');
  const [allTabDateTo, setAllTabDateTo]       = useState('');
  const [allTabClientId, setAllTabClientId]   = useState('');
  const [allTabStatuses, setAllTabStatuses]   = useState<AllStatus[]>([]);
  const [allTabTypes, setAllTabTypes]         = useState<('session' | 'package')[]>([]);
  const [allGroupByClient, setAllGroupByClient] = useState(false);
  const [allGroupByMonth, setAllGroupByMonth]   = useState(false);

  const loadSessions = useCallback(async () => {
    setSessLoading(true);
    setSessError('');
    try {
      const data = await getCashflowReport({
        date_from: dateFrom || undefined,
        date_to:   dateTo   || undefined,
        client_id: clientId || undefined,
      });
      setAllSessions(data.sessions);
    } catch {
      setSessError('Failed to load report.');
    } finally {
      setSessLoading(false);
    }
  }, [dateFrom, dateTo, clientId]);

  const loadPackages = useCallback(async () => {
    setPkgLoading(true);
    setPkgError('');
    try {
      const data = await getPackageCashflowReport({
        date_from: pkgDateFrom || undefined,
        date_to:   pkgDateTo   || undefined,
        client_id: pkgClientId || undefined,
      });
      setAllPkgPayments(data.payments);
    } catch {
      setPkgError('Failed to load package report.');
    } finally {
      setPkgLoading(false);
    }
  }, [pkgDateFrom, pkgDateTo, pkgClientId]);

  const loadUnified = useCallback(async () => {
    setAllTabLoading(true);
    setAllTabError('');
    try {
      const [sessData, pkgData] = await Promise.all([
        getCashflowReport({
          date_from: allTabDateFrom || undefined,
          date_to:   allTabDateTo   || undefined,
          client_id: allTabClientId || undefined,
        }),
        getPackageCashflowReport({
          date_from: allTabDateFrom || undefined,
          date_to:   allTabDateTo   || undefined,
          client_id: allTabClientId || undefined,
        }),
      ]);
      setAllTabSessions(sessData.sessions);
      setAllTabPkgs(pkgData.payments);
    } catch {
      setAllTabError('Failed to load unified report.');
    } finally {
      setAllTabLoading(false);
    }
  }, [allTabDateFrom, allTabDateTo, allTabClientId]);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { loadPackages(); }, [loadPackages]);
  useEffect(() => { loadUnified(); }, [loadUnified]);

  // Client-side status filter + derived summary for sessions
  // 'manual' shares the "Paid" filter with 'succeeded' since both display as Paid
  const sessions = sessStatuses.length === 0
    ? allSessions
    : allSessions.filter(s => {
        if (sessStatuses.includes(s.payment_status as SessStatus)) return true;
        if (s.payment_status === 'manual' && sessStatuses.includes('succeeded')) return true;
        return false;
      });

  const sessSummary = {
    total_sessions: sessions.length,
    total_collected_cents: sessions.reduce((sum, s) => {
      if (s.payment_status === 'succeeded' || s.payment_status === 'manual') {
        return sum + (s.amount_collected_cents ?? 0);
      }
      return sum;
    }, 0),
    total_outstanding_cents: sessions.reduce((sum, s) => {
      if (['unpaid', 'pending', 'failed', 'no_rate'].includes(s.payment_status)) {
        return sum + (s.rate_cents ?? 0);
      }
      return sum;
    }, 0),
  };

  // Client-side status filter + derived summary for packages
  const pkgPayments = pkgStatuses.length === 0
    ? allPkgPayments
    : allPkgPayments.filter(p => pkgStatuses.includes(p.payment_status as PkgStatus));

  const pkgSummary = {
    total_payments: pkgPayments.length,
    total_collected_cents: pkgPayments.reduce((sum, p) => ['paid', 'succeeded'].includes(p.payment_status) ? sum + p.amount_cents : sum, 0),
    total_outstanding_cents: pkgPayments.reduce((sum, p) => ['pending', 'failed'].includes(p.payment_status) ? sum + p.amount_cents : sum, 0),
  };

  const unifiedRows = useMemo((): UnifiedRow[] => {
    const sessRows: UnifiedRow[] = allTabSessions.map(s => ({
      id: s.appointment_id,
      rowType: 'session',
      date: s.scheduled_at,
      client_id: s.client_id,
      client_name: s.client_name,
      description: s.meeting_name,
      amount_cents: s.rate_cents,
      currency: s.currency,
      normalStatus: normalizeSessionStatus(s.payment_status),
      payment_method: s.payment_status === 'succeeded'
        ? 'Stripe'
        : s.payment_status === 'manual'
        ? (s.payment_method || 'Manual')
        : null,
      stripe_fee_cents: s.stripe_fee_cents,
      net_cents: s.net_cents,
      paid_at: s.paid_at,
      rawSession: s,
    }));

    const pkgRows: UnifiedRow[] = allTabPkgs.map(p => ({
      id: p.payment_id,
      rowType: 'package',
      date: p.due_date,
      client_id: p.client_id,
      client_name: p.client_name,
      description: `${p.package_name} — ${pkgPaymentTypeLabel(p.payment_type, p.installment_number)}`,
      amount_cents: p.amount_cents,
      currency: p.currency,
      normalStatus: normalizePkgStatus(p.payment_status),
      payment_method: p.payment_method || (p.stripe_payment_intent_id ? 'Stripe' : p.paid_at ? 'Manual' : null),
      stripe_fee_cents: p.stripe_fee_cents,
      net_cents: p.net_cents,
      paid_at: p.paid_at,
      rawPackage: p,
    }));

    const merged = [...sessRows, ...pkgRows];
    const filtered = merged
      .filter(r => allTabStatuses.length === 0 || allTabStatuses.includes(r.normalStatus))
      .filter(r => allTabTypes.length === 0 || allTabTypes.includes(r.rowType));

    return filtered.sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
  }, [allTabSessions, allTabPkgs, allTabStatuses, allTabTypes]);

  const unifiedSummary = useMemo(() => ({
    total: unifiedRows.length,
    collected_cents: unifiedRows.reduce((sum, r) => {
      if (r.normalStatus !== 'paid') return sum;
      return sum + (r.amount_cents ?? 0);
    }, 0),
    outstanding_cents: unifiedRows.reduce((sum, r) => {
      if (!['unpaid', 'pending', 'failed', 'no_rate'].includes(r.normalStatus)) return sum;
      return sum + (r.amount_cents ?? 0);
    }, 0),
  }), [unifiedRows]);

  function openAction(s: CashflowSession, type: ActionType) {
    setActionAppt(s);
    setActionType(type);
    setActionReason('');
    setActionChannel('manual');
    setActionError('');
  }

  function closeAction() {
    setActionAppt(null);
    setActionType(null);
    setActionReason('');
    setActionError('');
  }

  async function submitAction() {
    if (!actionAppt || !actionType) return;
    setActionLoading(true);
    setActionError('');
    try {
      if (actionType === 'record-failure') {
        if (!actionReason.trim()) {
          setActionError('Please enter a reason.');
          setActionLoading(false);
          return;
        }
        await recordPaymentFailure(actionAppt.appointment_id, actionReason.trim());
      } else {
        await refundSessionPayment(actionAppt.appointment_id, actionReason.trim() || 'Refund issued by coach', actionChannel);
      }
      closeAction();
      await loadSessions();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  const sessHasFilters = !!(dateFrom || dateTo || clientId || sessStatuses.length > 0);
  const pkgHasFilters  = !!(pkgDateFrom || pkgDateTo || pkgClientId || pkgStatuses.length > 0);

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">Cashflow</h1>
          <p className="text-sm text-gray-500 mt-0.5">Payment history and reconciliation</p>
        </div>
        <button
          onClick={() => {
            if (activeTab === 'all') exportUnifiedCsv(unifiedRows);
            else if (activeTab === 'sessions') exportSessionsCsv(sessions);
            else exportPackagesCsv(pkgPayments);
          }}
          disabled={activeTab === 'all' ? unifiedRows.length === 0 : activeTab === 'sessions' ? sessions.length === 0 : pkgPayments.length === 0}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-300 border border-dark-border rounded-lg hover:bg-dark-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-dark-border">
        {(['all', 'sessions', 'packages'] as TabId[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {tab === 'all' ? 'All' : tab === 'sessions' ? 'Sessions' : 'Packages'}
          </button>
        ))}
      </div>

      {/* ── ALL TAB ──────────────────────────────────────────────────────── */}
      {activeTab === 'all' && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Total</p>
              <p className="text-2xl font-bold text-white">{unifiedSummary.total}</p>
            </div>
            <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Collected (net)</p>
              <p className="text-2xl font-bold text-green-400">{formatCents(unifiedSummary.collected_cents)}</p>
            </div>
            <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Outstanding</p>
              <p className="text-2xl font-bold text-red-400">{formatCents(unifiedSummary.outstanding_cents)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {clients.length > 0 && (
              <select
                value={allTabClientId}
                onChange={e => setAllTabClientId(e.target.value)}
                className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
              >
                <option value="">All clients</option>
                {clients.map(c => (
                  <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">From</label>
              <input
                type="date"
                value={allTabDateFrom}
                onChange={e => setAllTabDateFrom(e.target.value)}
                className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">To</label>
              <input
                type="date"
                value={allTabDateTo}
                onChange={e => setAllTabDateTo(e.target.value)}
                className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
              />
            </div>
            <MultiSelectDropdown
              options={ALL_STATUSES}
              selected={allTabStatuses}
              onChange={setAllTabStatuses}
            />
            <MultiSelectDropdown
              options={[
                { value: 'session', label: 'Sessions' },
                { value: 'package', label: 'Packages' },
              ]}
              selected={allTabTypes}
              onChange={setAllTabTypes}
              placeholder="All types"
            />
            <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none ml-auto">
              <input
                type="checkbox"
                checked={allGroupByClient}
                onChange={e => { setAllGroupByClient(e.target.checked); if (e.target.checked) setAllGroupByMonth(false); }}
                className="accent-primary-500"
              />
              Group by client
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allGroupByMonth}
                onChange={e => { setAllGroupByMonth(e.target.checked); if (e.target.checked) setAllGroupByClient(false); }}
                className="accent-primary-500"
              />
              Group by month
            </label>
            {(allTabDateFrom || allTabDateTo || allTabClientId || allTabStatuses.length > 0 || allTabTypes.length > 0) && (
              <button
                onClick={() => { setAllTabDateFrom(''); setAllTabDateTo(''); setAllTabClientId(''); setAllTabStatuses([]); setAllTabTypes([]); }}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>

          {allTabError && <p className="text-sm text-red-400">{allTabError}</p>}

          {allTabLoading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : unifiedRows.length === 0 ? (
            <div className="text-sm text-gray-500">No records found for the selected filters.</div>
          ) : (
            <div className="bg-dark-sidebar border border-dark-border rounded-xl overflow-x-auto">
              <table className="min-w-max w-full text-xs">
                <thead>
                  <tr className="border-b border-dark-border text-gray-500 uppercase tracking-widest text-[10px]">
                    <th className="px-4 py-3 text-center font-medium">Date</th>
                    {!allGroupByClient && <th className="px-4 py-3 text-center font-medium">Client</th>}
                    <th className="px-4 py-3 text-center font-medium">Type</th>
                    <th className="px-4 py-3 text-center font-medium">Description</th>
                    <th className="px-4 py-3 text-center font-medium">Amount</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3 text-center font-medium">Payment Method</th>
                    <th className="px-4 py-3 text-center font-medium">Payment Date</th>
                    <th className="px-4 py-3 text-center font-medium">Stripe Fee</th>
                    <th className="px-4 py-3 text-center font-medium">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {(() => {
                    const renderRow = (r: UnifiedRow) => {
                      const { label, cls } = allStatusLabel(r.normalStatus);
                      const isOutstanding = ['unpaid', 'pending', 'failed', 'no_rate'].includes(r.normalStatus);
                      return (
                        <tr
                          key={r.id}
                          className={`transition-colors ${isOutstanding ? 'bg-red-950/10' : 'hover:bg-dark-hover'}`}
                        >
                          <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap">
                            {r.date ? formatDate(r.date) : '—'}
                          </td>
                          {!allGroupByClient && (
                            <td className="px-4 py-3 text-center text-gray-300 font-medium whitespace-nowrap">{r.client_name}</td>
                          )}
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded border text-[10px] font-medium ${
                              r.rowType === 'session'
                                ? 'bg-blue-900/30 text-blue-300 border-blue-800/50'
                                : 'bg-purple-900/30 text-purple-300 border-purple-800/50'
                            }`}>
                              {r.rowType === 'session' ? 'Session' : 'Package'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400 max-w-[200px] truncate">{r.description}</td>
                          <td className="px-4 py-3 text-center text-gray-300 font-medium">
                            {r.amount_cents != null ? formatCents(r.amount_cents, r.currency) : '—'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${cls}`}>
                              {label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap capitalize">
                            {r.payment_method || '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap">
                            {r.paid_at ? formatDateTime(r.paid_at) : '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-500">
                            {r.stripe_fee_cents != null ? formatCents(r.stripe_fee_cents, r.currency) : '—'}
                          </td>
                          <td className="px-4 py-3 text-center font-medium">
                            {r.normalStatus === 'paid'
                              ? <span className="text-green-400">{formatCents(r.net_cents ?? r.amount_cents ?? 0, r.currency)}</span>
                              : <span className="text-gray-600">—</span>}
                          </td>
                        </tr>
                      );
                    };

                    const TOTAL_COLS = allGroupByClient || allGroupByMonth ? 9 : 10;

                    const renderGroupHeader = (groupLabel: string, rows: UnifiedRow[]) => {
                      const subtotal = rows.reduce((sum, r) =>
                        r.normalStatus === 'paid' ? sum + (r.net_cents ?? r.amount_cents ?? 0) : sum, 0);
                      return (
                        <tr key={`group-${groupLabel}`} className="bg-dark-hover/60 border-t border-dark-border">
                          <td colSpan={TOTAL_COLS} className="px-4 py-2 text-xs font-semibold text-white">
                            {groupLabel}
                            <span className="ml-3 font-normal text-gray-500">{rows.length} item{rows.length !== 1 ? 's' : ''}</span>
                            <span className="ml-4 text-green-400">Collected: {formatCents(subtotal)}</span>
                          </td>
                        </tr>
                      );
                    };

                    if (allGroupByMonth) {
                      const monthGroups: { key: string; label: string; rows: UnifiedRow[] }[] = [];
                      const monthSeen = new Map<string, number>();
                      unifiedRows.forEach(r => {
                        const d = r.date ? new Date(r.date) : null;
                        const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'Unknown';
                        const mLabel = d ? d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : 'Unknown';
                        if (!monthSeen.has(key)) { monthSeen.set(key, monthGroups.length); monthGroups.push({ key, label: mLabel, rows: [] }); }
                        monthGroups[monthSeen.get(key)!].rows.push(r);
                      });
                      return monthGroups.flatMap(({ label: mLabel, rows }) => [renderGroupHeader(mLabel, rows), ...rows.map(renderRow)]);
                    }

                    if (allGroupByClient) {
                      const clientGroups: { name: string; id: string; rows: UnifiedRow[] }[] = [];
                      const clientSeen = new Map<string, number>();
                      unifiedRows.forEach(r => {
                        if (!clientSeen.has(r.client_id)) { clientSeen.set(r.client_id, clientGroups.length); clientGroups.push({ name: r.client_name, id: r.client_id, rows: [] }); }
                        clientGroups[clientSeen.get(r.client_id)!].rows.push(r);
                      });
                      return clientGroups.flatMap(({ name, rows }) => [renderGroupHeader(name, rows), ...rows.map(renderRow)]);
                    }

                    return unifiedRows.map(renderRow);
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── SESSIONS TAB ─────────────────────────────────────────────────── */}
      {activeTab === 'sessions' && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Sessions</p>
              <p className="text-2xl font-bold text-white">{sessSummary.total_sessions}</p>
            </div>
            <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Collected</p>
              <p className="text-2xl font-bold text-green-400">{formatCents(sessSummary.total_collected_cents)}</p>
            </div>
            <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Outstanding</p>
              <p className="text-2xl font-bold text-red-400">{formatCents(sessSummary.total_outstanding_cents)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {clients.length > 0 && (
              <select
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
              >
                <option value="">All clients</option>
                {clients.map(c => (
                  <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
              />
            </div>
            <MultiSelectDropdown
              options={SESS_STATUSES}
              selected={sessStatuses}
              onChange={setSessStatuses}
            />
            <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none ml-auto">
              <input
                type="checkbox"
                checked={groupByClient}
                onChange={e => { setGroupByClient(e.target.checked); if (e.target.checked) setGroupByMonth(false); }}
                className="accent-primary-500"
              />
              Group by client
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={groupByMonth}
                onChange={e => { setGroupByMonth(e.target.checked); if (e.target.checked) setGroupByClient(false); }}
                className="accent-primary-500"
              />
              Group by month
            </label>
            {sessHasFilters && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); setClientId(''); setSessStatuses([]); }}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>

          {sessError && <p className="text-sm text-red-400">{sessError}</p>}

          {sessLoading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="text-sm text-gray-500">No sessions found for the selected filters.</div>
          ) : (
            <div className="bg-dark-sidebar border border-dark-border rounded-xl overflow-x-auto">
              <table className="min-w-max w-full text-xs">
                <thead>
                  <tr className="border-b border-dark-border text-gray-500 uppercase tracking-widest text-[10px]">
                    <th className="px-4 py-3 text-center font-medium">Session Date</th>
                    {!groupByClient && <th className="px-4 py-3 text-center font-medium">Client</th>}
                    <th className="px-4 py-3 text-center font-medium">Session</th>
                    <th className="px-4 py-3 text-center font-medium">Session Fee</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3 text-center font-medium">Payment Method</th>
                    <th className="px-4 py-3 text-center font-medium">Payment Date</th>
                    <th className="px-4 py-3 text-center font-medium">Payment Received</th>
                    <th className="px-4 py-3 text-center font-medium">Stripe Fee</th>
                    <th className="px-4 py-3 text-center font-medium">Net</th>
                    <th className="px-4 py-3 text-center font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {(() => {
                    const renderRow = (s: CashflowSession) => {
                      const { label, cls } = statusLabel(s.payment_status);
                      const isOutstanding = ['unpaid', 'failed', 'no_rate'].includes(s.payment_status);
                      const canRefund = ['succeeded', 'manual'].includes(s.payment_status);
                      const canRecordFailure = ['unpaid', 'pending'].includes(s.payment_status);
                      return (
                        <tr
                          key={s.appointment_id}
                          className={`transition-colors ${isOutstanding ? 'bg-red-950/10' : 'hover:bg-dark-hover'}`}
                        >
                          <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap">
                            {s.scheduled_at ? formatDate(s.scheduled_at) : '—'}
                          </td>
                          {!groupByClient && <td className="px-4 py-3 text-center text-gray-300 font-medium">{s.client_name}</td>}
                          <td className="px-4 py-3 text-center text-gray-400 max-w-[180px] truncate">{s.meeting_name}</td>
                          <td className="px-4 py-3 text-center text-gray-400">
                            {s.rate_cents != null ? formatCents(s.rate_cents, s.currency) : <span className="text-gray-600 italic">—</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${cls}`}>
                              {label}
                            </span>
                            {s.failure_reason && (
                              <p className="text-[10px] text-red-400/70 mt-0.5 max-w-[140px] truncate" title={s.failure_reason}>
                                {s.failure_reason}
                              </p>
                            )}
                            {s.payment_status === 'refunded' && s.refund_reason && (
                              <p className="text-[10px] text-gray-500 mt-0.5 max-w-[140px] truncate" title={s.refund_reason}>
                                {s.refund_reason}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap capitalize">
                            {s.payment_status === 'succeeded'
                              ? 'Stripe'
                              : s.payment_status === 'manual'
                              ? (s.payment_method || 'Manual')
                              : <span className="text-gray-600">—</span>}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap text-xs">
                            {s.payment_status === 'refunded' && s.refunded_at
                              ? formatDateTime(s.refunded_at)
                              : s.paid_at
                              ? formatDateTime(s.paid_at)
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400">
                            {s.payment_status === 'refunded' && s.refunded_amount_cents != null
                              ? <span className="text-gray-500 line-through">{formatCents(s.refunded_amount_cents, s.currency)}</span>
                              : ['succeeded', 'manual'].includes(s.payment_status) && s.amount_collected_cents != null
                              ? formatCents(s.amount_collected_cents, s.currency)
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-500">
                            {s.stripe_fee_cents ? formatCents(s.stripe_fee_cents, s.currency) : '—'}
                          </td>
                          <td className="px-4 py-3 text-center font-medium">
                            {['succeeded', 'manual'].includes(s.payment_status) && s.net_cents != null
                              ? <span className="text-green-400">{formatCents(s.net_cents, s.currency)}</span>
                              : <span className="text-gray-600">—</span>
                            }
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              {canRecordFailure && (
                                <button
                                  onClick={() => openAction(s, 'record-failure')}
                                  className="px-2 py-1 text-[10px] font-medium text-red-400 border border-red-800/60 rounded hover:bg-red-900/30 transition-colors whitespace-nowrap"
                                >
                                  Record Failure
                                </button>
                              )}
                              {s.payment_status === 'failed' && (
                                <button
                                  onClick={() => openAction(s, 'record-failure')}
                                  className="px-2 py-1 text-[10px] font-medium text-yellow-400 border border-yellow-800/60 rounded hover:bg-yellow-900/30 transition-colors whitespace-nowrap"
                                >
                                  Update Reason
                                </button>
                              )}
                              {canRefund && (
                                <button
                                  onClick={() => openAction(s, 'refund')}
                                  className="px-2 py-1 text-[10px] font-medium text-gray-400 border border-dark-border rounded hover:bg-dark-hover transition-colors whitespace-nowrap"
                                >
                                  Refund
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    };

                    const FULL_COLS = groupByClient || groupByMonth ? 10 : 11;

                    const renderGroupHeader = (label: string, rows: CashflowSession[]) => {
                      const subtotalCollected = rows.reduce((sum, s) =>
                        (s.payment_status === 'succeeded' || s.payment_status === 'manual')
                          ? sum + ((s.amount_collected_cents ?? 0) - (s.platform_fee_cents ?? 0) - (s.stripe_fee_cents ?? 0))
                          : sum, 0);
                      return (
                        <tr key={`group-${label}`} className="bg-dark-hover/60 border-t border-dark-border">
                          <td colSpan={FULL_COLS} className="px-4 py-2 text-xs font-semibold text-white">
                            {label}
                            <span className="ml-3 font-normal text-gray-500">{rows.length} session{rows.length !== 1 ? 's' : ''}</span>
                            <span className="ml-4 text-green-400">Collected: {formatCents(subtotalCollected)}</span>
                          </td>
                        </tr>
                      );
                    };

                    if (groupByMonth) {
                      const monthGroups: { key: string; label: string; rows: CashflowSession[] }[] = [];
                      const monthSeen = new Map<string, number>();
                      sessions.forEach(s => {
                        const d = s.scheduled_at ? new Date(s.scheduled_at) : null;
                        const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'Unknown';
                        const label = d ? d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : 'Unknown';
                        if (!monthSeen.has(key)) {
                          monthSeen.set(key, monthGroups.length);
                          monthGroups.push({ key, label, rows: [] });
                        }
                        monthGroups[monthSeen.get(key)!].rows.push(s);
                      });
                      return monthGroups.flatMap(({ label, rows }) => [
                        renderGroupHeader(label, rows),
                        ...rows.map(renderRow),
                      ]);
                    }

                    if (!groupByClient) return sessions.map(renderRow);

                    const groups: { name: string; rows: CashflowSession[] }[] = [];
                    const seen = new Map<string, number>();
                    sessions.forEach(s => {
                      if (!seen.has(s.client_id)) {
                        seen.set(s.client_id, groups.length);
                        groups.push({ name: s.client_name, rows: [] });
                      }
                      groups[seen.get(s.client_id)!].rows.push(s);
                    });

                    return groups.flatMap(({ name, rows }) => [
                      renderGroupHeader(name, rows),
                      ...rows.map(renderRow),
                    ]);
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── PACKAGES TAB ─────────────────────────────────────────────────── */}
      {activeTab === 'packages' && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Payments</p>
              <p className="text-2xl font-bold text-white">{pkgSummary.total_payments}</p>
            </div>
            <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Collected</p>
              <p className="text-2xl font-bold text-green-400">{formatCents(pkgSummary.total_collected_cents)}</p>
            </div>
            <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Outstanding</p>
              <p className="text-2xl font-bold text-red-400">{formatCents(pkgSummary.total_outstanding_cents)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {clients.length > 0 && (
              <select
                value={pkgClientId}
                onChange={e => setPkgClientId(e.target.value)}
                className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
              >
                <option value="">All clients</option>
                {clients.map(c => (
                  <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">From</label>
              <input
                type="date"
                value={pkgDateFrom}
                onChange={e => setPkgDateFrom(e.target.value)}
                className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">To</label>
              <input
                type="date"
                value={pkgDateTo}
                onChange={e => setPkgDateTo(e.target.value)}
                className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
              />
            </div>
            <MultiSelectDropdown
              options={PKG_STATUSES}
              selected={pkgStatuses}
              onChange={setPkgStatuses}
            />
            {pkgHasFilters && (
              <button
                onClick={() => { setPkgDateFrom(''); setPkgDateTo(''); setPkgClientId(''); setPkgStatuses([]); }}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>

          {pkgError && <p className="text-sm text-red-400">{pkgError}</p>}

          {pkgLoading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : pkgPayments.length === 0 ? (
            <div className="text-sm text-gray-500">No package payments found for the selected filters.</div>
          ) : (
            <div className="bg-dark-sidebar border border-dark-border rounded-xl overflow-x-auto">
              <table className="min-w-max w-full text-xs">
                <thead>
                  <tr className="border-b border-dark-border text-gray-500 uppercase tracking-widest text-[10px]">
                    <th className="px-4 py-3 text-center font-medium">Client</th>
                    <th className="px-4 py-3 text-center font-medium">Package</th>
                    <th className="px-4 py-3 text-center font-medium">Payment</th>
                    <th className="px-4 py-3 text-center font-medium">Amount</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3 text-center font-medium">Payment Method</th>
                    <th className="px-4 py-3 text-center font-medium">Due Date</th>
                    <th className="px-4 py-3 text-center font-medium">Payment Date</th>
                    <th className="px-4 py-3 text-center font-medium">Fee</th>
                    <th className="px-4 py-3 text-center font-medium">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {pkgPayments.map(p => {
                    const { label, cls } = pkgStatusLabel(p.payment_status);
                    const isOutstanding = p.payment_status === 'pending' || p.payment_status === 'failed';
                    return (
                      <tr
                        key={p.payment_id}
                        className={`transition-colors ${isOutstanding ? 'bg-red-950/10' : 'hover:bg-dark-hover'}`}
                      >
                        <td className="px-4 py-3 text-center text-gray-300 font-medium">{p.client_name}</td>
                        <td className="px-4 py-3 text-center text-gray-400 max-w-[160px] truncate">{p.package_name}</td>
                        <td className="px-4 py-3 text-center text-gray-400">
                          {pkgPaymentTypeLabel(p.payment_type, p.installment_number)}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-300 font-medium">
                          {formatCents(p.amount_cents, p.currency)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${cls}`}>
                            {label}
                          </span>
                          {p.failure_reason && (
                            <p className="text-[10px] text-red-400/70 mt-0.5 max-w-[140px] truncate" title={p.failure_reason}>
                              {p.failure_reason}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap capitalize">
                          {p.payment_method || (p.stripe_payment_intent_id ? 'Stripe' : p.paid_at ? 'Manual' : '—')}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap">
                          {p.due_date ? formatDate(p.due_date) : '—'}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap">
                          {p.paid_at ? formatDateTime(p.paid_at) : '—'}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-500">
                          {p.stripe_fee_cents != null ? formatCents(p.stripe_fee_cents, p.currency) : '—'}
                        </td>
                        <td className="px-4 py-3 text-center font-medium">
                          {p.payment_status === 'paid'
                            ? <span className="text-green-400">
                                {p.net_cents != null
                                  ? formatCents(p.net_cents, p.currency)
                                  : formatCents(p.amount_cents, p.currency)}
                              </span>
                            : <span className="text-gray-600">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Action modal (sessions only) */}
      {actionAppt && actionType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-dark-card border border-dark-border rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-base font-semibold text-white mb-1">
              {actionType === 'record-failure' ? 'Record Payment Failure' : 'Issue Refund'}
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              {actionAppt.client_name} — {actionAppt.meeting_name}
              {actionAppt.scheduled_at ? ` — ${formatDate(actionAppt.scheduled_at)}` : ''}
            </p>

            {actionType === 'refund' && (
              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-1">Refund method</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                    <input
                      type="radio"
                      name="channel"
                      value="manual"
                      checked={actionChannel === 'manual'}
                      onChange={() => setActionChannel('manual')}
                      className="accent-primary-500"
                    />
                    Manual (record only)
                  </label>
                  {actionAppt.stripe_payment_intent_id && (
                    <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                      <input
                        type="radio"
                        name="channel"
                        value="stripe"
                        checked={actionChannel === 'stripe'}
                        onChange={() => setActionChannel('stripe')}
                        className="accent-primary-500"
                      />
                      Stripe (auto-refund)
                    </label>
                  )}
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-xs text-gray-400 mb-1">
                {actionType === 'record-failure' ? 'Reason (required)' : 'Reason (optional)'}
              </label>
              <textarea
                value={actionReason}
                onChange={e => setActionReason(e.target.value)}
                rows={3}
                className="w-full bg-dark-hover border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
                placeholder={
                  actionType === 'record-failure'
                    ? 'e.g. Card declined, cheque bounced...'
                    : 'e.g. Session cancelled, client request...'
                }
              />
            </div>

            {actionError && <p className="text-xs text-red-400 mb-3">{actionError}</p>}

            {actionType === 'refund' && (
              <p className="text-xs text-yellow-400/80 mb-4">
                This will issue a full refund of{' '}
                {actionAppt.amount_collected_cents != null
                  ? formatCents(actionAppt.amount_collected_cents, actionAppt.currency)
                  : 'the payment'}
                .
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={closeAction}
                disabled={actionLoading}
                className="px-4 py-1.5 text-xs text-gray-400 border border-dark-border rounded-lg hover:bg-dark-hover transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitAction}
                disabled={actionLoading}
                className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                  actionType === 'refund'
                    ? 'bg-red-700 hover:bg-red-600 text-white'
                    : 'bg-primary-600 hover:bg-primary-500 text-white'
                }`}
              >
                {actionLoading ? 'Processing...' : actionType === 'refund' ? 'Confirm Refund' : 'Record Failure'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashflowTab;
